import { Flex } from '@chakra-ui/layout'
import { Box } from '@chakra-ui/react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  MLHeaderActionbar,
  MLHeaderToolbar,
} from '../../components/MediaLibrary'
import { ContextMenu } from '../../components/MediaLibrary/ContextMenu'
import type { RightClickContextMenuRef } from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'
import RightClickContextMenu from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'
import PreviewPanel from '../../components/MediaLibrary/Details/PreviewPanel'
import { useMediaLibraryModals } from '../../components/MediaLibrary/hooks/useMediaLibraryModals'
import PollUnseenNotificationsCount from '../../components/MediaLibrary/Notifications/PollUnseenNotificationsCount'
import Panel from '../../components/MediaLibrary/Panel'
import PollOperations from '../../components/MediaLibrary/PollOperations'
import CollectionsView from '../../components/MediaLibrary/View/CollectionsView'
import { ViewableParentSuccessResponse } from '../../interfaces'
import { MLContext, useUserPreferences } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { MainContentHeadingBox } from '../Base/MainContent'

const MLMainContentHeadingBox = styled(MainContentHeadingBox)`
  min-height: initial;
  max-height: initial;
  padding-top: 12px;
  padding-bottom: 12px;
`

const MediaCollectionsLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const [state, dispatch] = useContext(MLContext)

  const contextMenuRef = useRef<RightClickContextMenuRef | null>(null)

  const controllers = useMediaLibraryModals()

  const resetSearch = useCallback(() => {
    dispatch([
      {
        type: MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH,
      },
      {
        type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.SET_Q,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_MODE,
        payload: 'keyword',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_INPUT_MODE,
        payload: 'builder',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_BY_TAGS,
        payload: true,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_FILE_TYPE,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
      {
        type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
        payload: false,
      },
    ])
  }, [state.searchQuery])

  const title = useMemo(() => {
    if (state.searchQuery) {
      return t('search_results', 'Search Results')
    }

    if (
      !state.currentMediaCollection &&
      !(state.viewableParent as ViewableParentSuccessResponse).mediaCollection
    ) {
      return t('media_collections', 'Media collections')
    }

    const folder = state.folderPath.split('/').pop()

    return [
      (state.viewableParent as ViewableParentSuccessResponse).mediaCollection
        ?.name,
      folder,
    ]
      .filter(x => x)
      .join(' / ')
  }, [
    state.folderPath,
    state.currentMediaCollection,
    state.viewableParent,
    state.searchQuery,
  ])

  const onClickForMain = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      e.stopPropagation()
      contextMenuRef.current?.closeMenu()
    },
    [],
  )

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if ((e.target as HTMLElement).tagName === 'DIV') {
        e.preventDefault()
        e.stopPropagation()
        contextMenuRef.current?.handleContextMenu(e.nativeEvent)
      }
    },
    [contextMenuRef.current],
  )
  const scrollRef = useRef<HTMLDivElement>(null)

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    setScrollEl(scrollRef.current)
  }, [])

  useEffect(() => {
    const handleScroll = (event: Event) => {
      dispatch({ type: MediaLibraryActions.CLOSE_CONTEXT_MENU })
    }
    const element = scrollRef.current
    
    if (element) {
      element.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])
  const { prefs } = useUserPreferences()

  return (
    <>
      <Helmet title={title} />
      <Flex
        direction="column"
        alignItems="stretch"
        height="100%"
        onContextMenu={handleContextMenu}
        onClick={onClickForMain}
      >
        <MLMainContentHeadingBox>
          <MLHeaderActionbar resetSearch={resetSearch} />
        </MLMainContentHeadingBox>
        <MLHeaderToolbar resetSearch={resetSearch} controllers={controllers} />
        <ContextMenu controllers={controllers} />
        <Flex direction="row" overflow="hidden">
          <Box
            height="calc(100vh - 60px - 58px - 24px)" //action bar + toolbar + pollOperations
            ref={scrollRef}
            flex={1}
            overflow={'auto'}
            className="media-library-layout"
            sx={{
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
            }}
          >
            <Box>
              {' '}
              {/* Do not remove this wrapper, on removing it background state would not be maintained*/}
              <RightClickContextMenu ref={contextMenuRef} />
              <CollectionsView
                scrollEl={scrollEl}
                minHeight="calc(100vh - 60px - 58px - 24px)"
              />
            </Box>
          </Box>
          {!isPublicLink && prefs.previewVisible && (
            <Box flexShrink={0} w="377px">
              <PreviewPanel isDetailPage={false} />
            </Box>
          )}
        </Flex>
        <PollOperations />
        {!isPublicLink && <PollUnseenNotificationsCount />}
      </Flex>
      <Panel />
    </>
  )
}

export default MediaCollectionsLayout
